import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Wallet, Upload, FileText, Loader2, Eye } from "lucide-react";
import { DocPreview, DocHistory } from "@/components/docs";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg, fmtDate, inr, today, daysUntil } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { uploadFile } from "@/lib/compress";
import { useDriverRules, RULE_CHECKS, CompletionBadge, DriverRulesDialog } from "@/components/driverRules";

const req = (rules, k, text) => <>{text}{rules?.[k] && <span className="text-rose-600"> *</span>}</>;
const ruleErrors = (rules, labels, d, only) => Object.keys(RULE_CHECKS).filter((k) => rules?.[k] && (!only || only.includes(k)) && !RULE_CHECKS[k](d)).map((k) => labels[k] || k);
import { PageHeader, StatusBadge, SearchBox, Field, TableWrap, Empty, th, td, tr } from "@/components/common";

const EMPTY = {
  name: "", phone: "", license_number: "", license_expiry: "", license_photo_id: null, driver_photo_id: null,
  start_km: "", family_contacts: [{ name: "", phone: "" }, { name: "", phone: "" }],
  current_vehicle_id: "", left_vehicle_km: "", employment_status: "Active", blacklist_reason: "", monthly_salary: 0,
  per_day_rate: "", per_km_rate: "", km_mode: "actual", target_km: "", overtime_rate: "", address: "",
  pan_number: "", bank_account_number: "", bank_ifsc: "", bank_name: "", bank_holder: "",
};

export function FilePick({ label, refType, value, onChange, testid, required = false }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [view, setView] = useState(false);
  useEffect(() => {
    if (!value) { setPreview(null); return undefined; }
    let url;
    api.get(`/files/${value}`, { responseType: "blob" }).then((r) => {
      if (r.data.type.startsWith("image/")) { url = URL.createObjectURL(r.data); setPreview(url); } else setPreview("pdf");
    }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [value]);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const { data } = await uploadFile(`/uploads?ref_type=${refType}`, file);
      onChange(data.id);
      toast.success("File uploaded");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div>
      <Label className="text-xs font-semibold text-muted-foreground">{label}{required && <span className="text-rose-600"> *</span>}</Label>
      <button type="button" data-testid={testid} onClick={() => ref.current.click()}
        className="mt-1.5 flex h-24 w-full items-center justify-center overflow-hidden rounded-lg border border-dashed bg-secondary/40 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-foreground">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" />
          : preview && preview !== "pdf" ? <img src={preview} alt="" className="h-full w-full object-cover" />
          : preview === "pdf" ? <span className="flex items-center gap-1"><FileText className="h-4 w-4" /> PDF uploaded — tap to replace</span>
          : <span className="flex items-center gap-1"><Upload className="h-4 w-4" /> Upload PNG / JPG / PDF</span>}
      </button>
      {value && <div className="mt-1 flex gap-1">
        <button type="button" title="View" data-testid={`${testid}-view`} onClick={() => setView(true)} className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"><Eye className="h-4 w-4" /></button>
        <button type="button" title="Replace" data-testid={`${testid}-replace`} onClick={() => ref.current.click()} className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"><Pencil className="h-4 w-4" /></button>
        <button type="button" title="Remove" data-testid={`${testid}-remove`} onClick={() => window.confirm("Remove this file?") && onChange(null)} className="rounded p-1 text-muted-foreground hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
      </div>}
      {view && <DocPreview fileId={value} title={label} onClose={() => setView(false)} />}
      <input ref={ref} type="file" accept="image/png,image/jpeg,application/pdf" className="hidden" onChange={pick} data-testid={`${testid}-input`} />
    </div>
  );
}

export function DriverDocsDialog({ driver, onClose, onSaved, rules = {}, labels = {} }) {
  const [f, setF] = useState({});
  const [k, setK] = useState(0);
  useEffect(() => { if (driver) setF({ license_number: driver.license_number || "", license_expiry: driver.license_expiry || "", license_photo_id: driver.license_photo_id, driver_photo_id: driver.driver_photo_id }); }, [driver]);
  const save = async (e) => {
    e.preventDefault();
    const miss = ruleErrors(rules, labels, f, ["license", "license_photo", "driver_photo"]);
    if (miss.length) return toast.error(`Required: ${miss.join(", ")}`);
    try { await api.patch(`/drivers/${driver.id}/docs`, f); toast.success("Documents updated"); setK(k + 1); onSaved(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={!!driver} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-card max-h-[90vh] overflow-y-auto" data-testid="driver-docs-dialog">
        <DialogHeader><DialogTitle>{driver?.name} — Documents</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-3 sm:grid-cols-2">
          <Field label={req(rules, "license", "DL Number")}><Input data-testid="ddoc-dl-number" value={f.license_number || ""} onChange={(e) => setF({ ...f, license_number: e.target.value })} /></Field>
          <Field label={req(rules, "license", "DL Expiry")}><Input type="date" data-testid="ddoc-dl-expiry" value={f.license_expiry || ""} onChange={(e) => setF({ ...f, license_expiry: e.target.value })} /></Field>
          <FilePick label="License Photo" required={rules.license_photo} refType="driver_license" value={f.license_photo_id} onChange={(id) => setF((x) => ({ ...x, license_photo_id: id }))} testid="ddoc-license" />
          <FilePick label="Driver Photo" required={rules.driver_photo} refType="driver_photo" value={f.driver_photo_id} onChange={(id) => setF((x) => ({ ...x, driver_photo_id: id }))} testid="ddoc-photo" />
          <DialogFooter className="sm:col-span-2"><Button type="button" variant="outline" onClick={onClose}>Close</Button><Button type="submit" data-testid="ddoc-save-btn">Save Documents</Button></DialogFooter>
        </form>
        {driver && <DocHistory entity="driver" refId={driver.id} reloadKey={k} />}
      </DialogContent>
    </Dialog>
  );
}

export function DriverAvatar({ fileId, name }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!fileId) { setSrc(null); return undefined; }
    let url;
    api.get(`/files/${fileId}`, { responseType: "blob" }).then((r) => {
      if (r.data.type.startsWith("image/")) { url = URL.createObjectURL(r.data); setSrc(url); }
    }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [fileId]);
  if (src) return <img src={src} alt={name} className="h-9 w-9 shrink-0 rounded-full object-cover" />;
  return <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-bold text-accent-foreground">{name?.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}</div>;
}

function FormSection({ title, children }) {
  return (
    <div className="rounded-lg border p-4">
      <div className="label-xs mb-3">{title}</div>
      <div className="grid grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

export function DriverDialog({ open, onClose, initial, onSaved, showSalary = true }) {
  const rcd = useDriverRules();
  const { rules, labels } = rcd;
  const { role: myRole } = useAuth();
  const [f, setF] = useState(EMPTY);
  const [vehicles, setVehicles] = useState([]);
  useEffect(() => {
    if (!open) return;
    setF(initial ? { ...EMPTY, ...initial, left_vehicle_km: initial.left_vehicle_km ?? "", family_contacts: [0, 1].map((i) => initial.family_contacts?.[i] || EMPTY.family_contacts[i]) } : EMPTY);
    api.get("/vehicles").then((r) => setVehicles(r.data));
  }, [initial, open]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setContact = (i, k) => (e) => {
    const fc = [...f.family_contacts];
    fc[i] = { ...fc[i], [k]: e.target.value };
    setF({ ...f, family_contacts: fc });
  };
  const save = async () => {
    const miss = ruleErrors(rules, labels, f);
    if (miss.length) return toast.error(`Required: ${miss.join(", ")}`);
    if (f.employment_status === "Blacklisted" && !f.blacklist_reason.trim()) return toast.error("Reason for blacklisting is required");
    try {
      const body = {
        name: f.name, phone: f.phone, license_number: f.license_number, license_expiry: f.license_expiry,
        license_photo_id: f.license_photo_id, driver_photo_id: f.driver_photo_id,
        start_km: Number(f.start_km || 0), family_contacts: f.family_contacts,
        current_vehicle_id: f.current_vehicle_id || null,
        left_vehicle_km: f.left_vehicle_km === "" ? null : Number(f.left_vehicle_km),
        employment_status: f.employment_status, blacklist_reason: f.employment_status === "Blacklisted" ? f.blacklist_reason : "", monthly_salary: Number(f.monthly_salary),
        per_day_rate: Number(f.per_day_rate || 0), per_km_rate: Number(f.per_km_rate || 0),
        km_mode: f.km_mode || "actual", target_km: Number(f.target_km || 0), overtime_rate: Number(f.overtime_rate || 0), address: f.address,
        pan_number: f.pan_number || "", bank_account_number: f.bank_account_number || "", bank_ifsc: f.bank_ifsc || "", bank_name: f.bank_name || "", bank_holder: f.bank_holder || "",
      };
      if (initial?.id) await api.put(`/drivers/${initial.id}`, body);
      else await api.post("/drivers", body);
      toast.success("Driver saved");
      onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-card max-h-[90vh] overflow-y-auto" data-testid="driver-dialog">
        <DialogHeader className="flex-row flex-wrap items-center justify-between gap-2 pr-8"><DialogTitle>{initial?.id ? "Edit Driver" : "Add Driver"}</DialogTitle>
          {myRole === "admin" && <DriverRulesDialog cfg={rcd} onSaved={rcd.setCfg} testid="driver-form-field-settings" />}
        </DialogHeader>
        <div className="space-y-4">
          <FormSection title="1 · Personal & License Info">
            <Field label={req(rules, "name", "Full Name")}><Input data-testid="driver-name-input" value={f.name} onChange={set("name")} /></Field>
            <Field label={req(rules, "phone", "Mobile Number")}><Input data-testid="driver-phone-input" value={f.phone} onChange={set("phone")} /></Field>
            <Field label={req(rules, "license", "License Number")}><Input data-testid="driver-license-input" value={f.license_number} onChange={set("license_number")} /></Field>
            <Field label={req(rules, "license", "License Expiry")}><Input data-testid="driver-license-expiry-input" type="date" value={f.license_expiry} onChange={set("license_expiry")} /></Field>
            {showSalary && <Field label="Monthly Salary (₹)"><Input data-testid="driver-salary-input" type="number" value={f.monthly_salary} onChange={set("monthly_salary")} /></Field>}
            <Field label="Per-Day Rate (₹ — blank = Monthly Salary ÷ 30)"><Input data-testid="driver-per-day-rate-input" type="number" min="0" placeholder="e.g. 1000" value={f.per_day_rate} onChange={set("per_day_rate")} /></Field>
            <Field label="KM Allowance Mode">
              <select data-testid="driver-km-mode-select" className="native-select" value={f.km_mode || "actual"} onChange={set("km_mode")}>
                <option value="actual">Actual KM × Rate</option>
                <option value="tiered">Tiered — Overtime beyond Target KM</option>
              </select>
            </Field>
            <Field label="Per-KM Rate (₹/KM)"><Input data-testid="driver-per-km-rate-input" type="number" min="0" step="0.01" placeholder="e.g. 2.00" value={f.per_km_rate} onChange={set("per_km_rate")} /></Field>
            {f.km_mode === "tiered" && (
              <>
                <Field label="Target KM Limit"><Input data-testid="driver-target-km-input" type="number" min="0" placeholder="e.g. 300" value={f.target_km} onChange={set("target_km")} /></Field>
                <Field label="Overtime Rate (₹/KM beyond target)"><Input data-testid="driver-overtime-rate-input" type="number" min="0" step="0.01" placeholder="e.g. 3.50" value={f.overtime_rate} onChange={set("overtime_rate")} /></Field>
              </>
            )}
            <Field label={req(rules, "address", "Permanent Address")}><Input data-testid="driver-address-input" value={f.address} onChange={set("address")} /></Field>
            <FilePick label="License Photo" required={rules.license_photo} refType="driver_license" testid="driver-license-photo-btn" value={f.license_photo_id} onChange={(id) => setF({ ...f, license_photo_id: id })} />
            <FilePick label="Driver Photo" required={rules.driver_photo} refType="driver_photo" testid="driver-photo-btn" value={f.driver_photo_id} onChange={(id) => setF({ ...f, driver_photo_id: id })} />
          </FormSection>
          <FormSection title={req(rules, "emergency_contact", "2 · Emergency Family Contacts (at least 1)")}>
            <Field label="Family Member 1 — Name"><Input data-testid="family-1-name-input" value={f.family_contacts[0].name} onChange={setContact(0, "name")} /></Field>
            <Field label="Family Member 1 — Phone"><Input data-testid="family-1-phone-input" value={f.family_contacts[0].phone} onChange={setContact(0, "phone")} /></Field>
            <Field label="Family Member 2 — Name"><Input data-testid="family-2-name-input" value={f.family_contacts[1].name} onChange={setContact(1, "name")} /></Field>
            <Field label="Family Member 2 — Phone"><Input data-testid="family-2-phone-input" value={f.family_contacts[1].phone} onChange={setContact(1, "phone")} /></Field>
          </FormSection>
          <FormSection title="3 · PAN & Bank Details">
            <Field label={req(rules, "pan", "PAN Number")}><Input data-testid="driver-pan-input" className="uppercase" maxLength={10} value={f.pan_number || ""} onChange={set("pan_number")} placeholder="ABCDE1234F" /></Field>
            <Field label={req(rules, "bank", "Account Holder Name")}><Input data-testid="driver-bank-holder-input" value={f.bank_holder || ""} onChange={set("bank_holder")} /></Field>
            <Field label={req(rules, "bank", "Bank Account Number")}><Input data-testid="driver-bank-account-input" value={f.bank_account_number || ""} onChange={set("bank_account_number")} /></Field>
            <Field label={req(rules, "bank", "IFSC")}><Input data-testid="driver-bank-ifsc-input" className="uppercase" maxLength={11} value={f.bank_ifsc || ""} onChange={set("bank_ifsc")} /></Field>
            <Field label={req(rules, "bank", "Bank Name")}><Input data-testid="driver-bank-name-input" value={f.bank_name || ""} onChange={set("bank_name")} /></Field>
          </FormSection>
          <FormSection title="4 · Vehicle Assignment & Odometer">
            <Field label="Current Vehicle">
              <select data-testid="driver-vehicle-select" className="native-select" value={f.current_vehicle_id || ""} onChange={set("current_vehicle_id")}>
                <option value="">— Not assigned —</option>
                {vehicles.map((v) => <option key={v.id} value={v.id}>{v.vehicle_number} · {v.status}</option>)}
              </select>
            </Field>
            <Field label="Driver Status">
              <select data-testid="driver-employment-status-select" className="native-select" value={f.employment_status} onChange={set("employment_status")}>
                <option value="Active">Active</option>
                <option value="Left">Left (Resigned / Inactive)</option>
                <option value="Blacklisted">Blacklisted</option>
              </select>
            </Field>
            {f.employment_status === "Blacklisted" && (
              <Field label="Reason for Blacklisting *" className="col-span-2">
                <Input data-testid="blacklist-reason-input" className="border-rose-300" value={f.blacklist_reason} onChange={set("blacklist_reason")} placeholder="e.g. Fraud, Vehicle Damage, Misbehavior, Goods Theft" />
              </Field>
            )}
            <Field label="Joining Start KM"><Input data-testid="driver-start-km-input" type="number" value={f.start_km} onChange={set("start_km")} /></Field>
            <Field label="Left Vehicle KM (when leaving)"><Input data-testid="driver-left-km-input" type="number" value={f.left_vehicle_km} onChange={set("left_vehicle_km")} /></Field>
          </FormSection>
        </div>
        <DialogFooter><Button data-testid="driver-save-btn" onClick={save}>Save Driver</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function LedgerDialog({ driver, onClose, onSaved }) {
  const [txns, setTxns] = useState([]);
  const [f, setF] = useState({ type: "advance", amount: "", date: today(), note: "", month: today().slice(0, 7) });
  const load = () => driver && api.get(`/drivers/${driver.id}/transactions`).then((r) => setTxns(r.data));
  useEffect(() => { load(); }, [driver]); // eslint-disable-line
  const add = async () => {
    try {
      await api.post(`/drivers/${driver.id}/transactions`, { ...f, amount: Number(f.amount), date: f.type === "salary" ? `${f.month}-${new Date(+f.month.slice(0, 4), +f.month.slice(5, 7), 0).getDate()}` : f.date });
      toast.success("Entry added");
      setF({ ...f, amount: f.type === "salary" && driver?.monthly_salary ? String(driver.monthly_salary) : "", note: "" });
      load();
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const delTxn = async (t) => {
    if (!window.confirm(`Delete monthly salary ${t.month}?`)) return;
    try { await api.delete(`/drivers/${driver.id}/transactions/${t.id}`); toast.success("Deleted"); load(); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!driver} onOpenChange={onClose}>
      <DialogContent className="max-w-xl bg-card" data-testid="driver-ledger-dialog">
        <DialogHeader><DialogTitle>{driver?.name} · Advances & Salary</DialogTitle></DialogHeader>
        {driver && (
          <div className="grid grid-cols-2 gap-3 text-center sm:grid-cols-4">
            {[["Trips Completed", driver.completed_trips, false], ["Monthly Salary Added", driver.total_salary || 0, true], ["Advances Given", driver.total_advances, true], ["Salary Paid", driver.total_paid, true]].map(([l, v, money]) => (
              <div key={l} className="rounded-lg border bg-secondary/50 p-3"><div className="label-xs">{l}</div><div className="mt-1 amount font-semibold">{money ? inr(v) : v}</div></div>
            ))}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-[1.6fr_1fr_1fr_auto]">
          <select data-testid="txn-type-select" className="native-select" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value, amount: e.target.value === "salary" && !f.amount && driver?.monthly_salary ? String(driver.monthly_salary) : f.amount })}>
            <option value="advance">Advance</option><option value="salary">Monthly Salary (jama)</option><option value="salary_payment">Salary Payment</option>
          </select>
          <Input data-testid="txn-amount-input" type="number" placeholder="Amount" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} />
          {f.type === "salary" ? <Input data-testid="txn-month-input" type="month" value={f.month} onChange={(e) => setF({ ...f, month: e.target.value })} />
            : <Input data-testid="txn-date-input" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />}
          <Button data-testid="txn-add-btn" onClick={add}>Add</Button>
          <Input data-testid="txn-note-input" className="col-span-2 sm:col-span-4" placeholder="Note" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        </div>
        <div className="max-h-56 divide-y overflow-y-auto rounded-lg border">
          {txns.length === 0 && <Empty text="No entries yet" />}
          {txns.map((t) => (
            <div key={t.id} className="flex items-center justify-between px-3 py-2 text-sm">
              <div><div className="font-medium">{t.type === "advance" ? "Advance" : t.type === "salary" ? `Monthly Salary · ${t.month}` : t.type === "recovery" ? "Recovered from Driver" : "Salary Payment"}</div><div className="text-xs text-muted-foreground">{fmtDate(t.date)} {t.note && `· ${t.note}`}</div></div>
              <span className="flex items-center gap-2"><span className="amount">{inr(t.amount)}</span>
                {t.type === "salary" && <button data-testid={`txn-delete-${t.id}`} onClick={() => delTxn(t)} className="rounded p-1 text-muted-foreground hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>}</span>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Drivers() {
  const { role } = useAuth();
  const navigate = useNavigate();
  const isAdmin = role === "admin";
  const [docs, setDocs] = useState(null);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [avail, setAvail] = useState("");
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const [ledger, setLedger] = useState(null);
  const rc = useDriverRules();
  const load = () => api.get("/drivers").then((r) => { setItems(r.data); setLedger((l) => (l ? r.data.find((d) => d.id === l.id) : l)); });
  useEffect(() => { load(); }, []);
  const rows = useMemo(() => items.filter((d) => {
    if (avail === "on" && !d.on_trip) return false;
    if (avail === "free" && (d.on_trip || d.employment_status !== "Active")) return false;
    if (["Active", "Left", "Blacklisted"].includes(avail) && d.employment_status !== avail) return false;
    return `${d.name} ${d.phone} ${d.license_number}`.toLowerCase().includes(q.toLowerCase());
  }), [items, q, avail]);
  const del = async (d) => {
    if (!window.confirm(`Delete ${d.name}?`)) return;
    try { await api.delete(`/drivers/${d.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const heads = ["Driver", "Phone", "License", "Expiry", "Joining KM", "Vehicle", ...(isAdmin ? ["Advances"] : []), "Status", ""];

  return (
    <div>
      <PageHeader title="Drivers" subtitle={`${items.length} drivers · ${items.filter((d) => d.on_trip).length} on trip`}>
        {isAdmin && <DriverRulesDialog cfg={rc} onSaved={rc.setCfg} />}
        {["admin", "manager", "operation"].includes(role) && <Button data-testid="add-driver-btn" onClick={() => { setEdit(null); setOpen(true); }}><Plus className="mr-1 h-4 w-4" />Add Driver</Button>}
      </PageHeader>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row">
        <SearchBox testid="driver-search-input" value={q} onChange={setQ} placeholder="Search name, phone, license…" />
        <select data-testid="driver-availability-filter" className="native-select sm:w-44" value={avail} onChange={(e) => setAvail(e.target.value)}>
          <option value="">All drivers</option><option value="Active">Active</option><option value="on">On trip</option><option value="free">Available</option><option value="Left">Left</option><option value="Blacklisted">Blacklisted</option>
        </select>
      </div>
      <TableWrap>
        <thead><tr>{heads.map((h, i) => <th key={i} className={th}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((d) => {
            const exp = daysUntil(d.license_expiry);
            return (
              <tr key={d.id} className={`${tr} cursor-pointer`} onClick={() => navigate(`/drivers/${d.id}`)} data-testid={`driver-row-${d.id}`}>
                <td className={`${td} font-semibold`}>
                  <div className="flex items-center gap-2.5">
                    <DriverAvatar fileId={d.driver_photo_id} name={d.name} />
                    <div>{d.name}{d.user_id && <span className="ml-2 rounded bg-accent px-1.5 py-0.5 text-[10px] font-semibold text-accent-foreground">APP</span>}
                      <div className="text-xs font-normal text-muted-foreground">{d.family_contacts?.[0] ? `Kin: ${d.family_contacts[0].name}` : ""}</div>
                      <CompletionBadge driver={d} labels={rc.labels} testid={`driver-completion-${d.id}`} />
                    </div>
                  </div>
                </td>
                <td className={`${td} font-mono text-xs`}>{d.phone}</td>
                <td className={`${td} font-mono text-xs`}>
                  {d.license_number}
                  {d.license_photo_id && <button data-testid={`license-doc-${d.id}`} onClick={async () => (await import("@/lib/api")).openFile(d.license_photo_id).catch(() => {})} className="ml-1 inline-flex align-middle text-primary hover:underline" title="View license"><FileText className="h-3.5 w-3.5" /></button>}
                </td>
                <td className={`${td} ${exp !== null && exp <= 30 ? "font-semibold text-rose-600" : ""}`}>{fmtDate(d.license_expiry)}</td>
                <td className={`${td} amount`}>{d.start_km?.toLocaleString("en-IN")} km</td>
                <td className={`${td} font-mono text-xs`}>{d.current_vehicle_number || "-"}{d.employment_status === "Left" && d.left_vehicle_km != null && <div className="text-muted-foreground">Left at {Number(d.left_vehicle_km).toLocaleString("en-IN")} km</div>}</td>
                {isAdmin && <td className={`${td} amount`}>{inr(d.total_advances)}</td>}
                <td className={td}><StatusBadge status={d.employment_status === "Blacklisted" ? "Blacklisted" : d.employment_status === "Left" ? "Left" : d.on_trip ? "On Trip" : "Available"} /></td>
                <td className={`${td} text-right`} onClick={(e) => e.stopPropagation()}>
                  {["admin", "manager", "operation"].includes(role) && <button data-testid={`docs-driver-${d.id}`} onClick={() => setDocs(d)} className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground" title="Documents (DL, photo)"><FileText className="h-4 w-4" /></button>}
                  {isAdmin && (
                    <>
                      <button data-testid={`ledger-driver-${d.id}`} onClick={() => setLedger(d)} className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground" title="Advances"><Wallet className="h-4 w-4" /></button>
                      <button data-testid={`edit-driver-${d.id}`} onClick={() => { setEdit(d); setOpen(true); }} className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"><Pencil className="h-4 w-4" /></button>
                      <button data-testid={`delete-driver-${d.id}`} onClick={() => del(d)} className="rounded p-1.5 text-muted-foreground hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </TableWrap>
      {rows.length === 0 && <Empty testid="drivers-empty" />}
      <DriverDocsDialog driver={docs} onClose={() => setDocs(null)} onSaved={() => load()} rules={rc.rules} labels={rc.labels} />
      <DriverDialog key={JSON.stringify(rc.rules)} open={open} onClose={() => setOpen(false)} initial={edit} onSaved={load} showSalary={isAdmin} />
      <LedgerDialog driver={ledger} onClose={() => setLedger(null)} onSaved={load} />
    </div>
  );
}
